import { useContext, useEffect, useState, Suspense, lazy } from 'react';
import './App.css';
import Navbar from './Components/Common/Navbar Section/Navbar';
import SideBar from './Components/Common/Sidebar Section/SideBar';
import Footer from './Components/Common/Footer Section/Footer';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import ScrollToTop from './Components/Common/ScrollToTop';
import CartSidebar from './Components/Common/Cart SideBar/CartSidebar';
import { gapi } from 'gapi-script';
import { useDispatch, useSelector } from 'react-redux';
import { addSelectedCategory, changeLanguage, deleteItem } from './Components/redux/productSlice';
import AuthContext from './Components/Common/authContext';
import CartPopupModal from './Components/Common/Cart Popup/CartPopup';
import { ToastContainer } from 'react-toastify';
import ErrorPage from './Components/Common/ErrorPage';
import axios from 'axios';
import { Helmet } from 'react-helmet';

// Critical components (loaded immediately)
import HomeOptimized from './Components/Home page/HomeOptimized';
import ResendVerifyPage from './Components/Verify Page/ResendVerifyPage';
import AddCartPopup from './Components/Common/Add To Cart Popup/AddCartPopup';

// Lazy load non-critical page components
const BookDetailsPage = lazy(() => import('./Components/Book Details Page/BookDetailsPage'));
const BooksPage = lazy(() => import('./Components/Books page/BooksPage'));
const Cart = lazy(() => import('./Components/Cart Page/Cart'));
const Favorite = lazy(() => import('./Components/Favorite Page/Favorite'));
const Login = lazy(() => import('./Components/Login Page/Login'));
const Compare = lazy(() => import('./Components/Compare Page/Compare'));
const Register = lazy(() => import('./Components/Register page/Register'));
const ContactUsPage = lazy(() => import('./Components/Contact us Page/ContactPage'));
const OrderTracking = lazy(() => import('./Components/Order Tracking/OrderTracking'));
const FollowOrder = lazy(() => import('./Components/Order Tracking/Follow Order Page/FollowOrder'));
const Review = lazy(() => import('./Components/Order Tracking/Review page/Review'));
const AcountProfile = lazy(() => import('./Components/Account Profile Page/AcountProfile'));
const Event = lazy(() => import('./Components/Event Page/Event'));
const EventDetails = lazy(() => import('./Components/Event Page/Event Details Page/EventDetails'));
const CheckOut = lazy(() => import('./Components/CheckOut Page/CheckOut'));
const ResetPassword = lazy(() => import('./Components/Reset Password Page/ResetPassword'));
const AddReturnRefund = lazy(() => import('./Components/Return Refund Page/Add Return Reund/AddReturnRefund'));
const RetourHistory = lazy(() => import('./Components/Return Refund Page/Retour History/RetourHistory'));
const ViewRetour = lazy(() => import('./Components/Return Refund Page/View Return Refund/ViewRetour'));
const Librairie = lazy(() => import('./Components/Librairie Page/Librairie'));
const ResetPasswordEmail = lazy(() => import('./Components/Reset Password Email Page/ResetPasswordEmail'));
const VerifyPage = lazy(() => import('./Components/Verify Page/VerifyPage'));
const CollectionsPage = lazy(() => import('./Components/Collection Pages/Collections Page/Collections'));
const CollectionDetailsPage = lazy(() => import('./Components/Collection Pages/CollectionDetails Page/CollectionDetails'));
const Collaborators = lazy(() => import('./Components/Collaborators/Collaborators Page/Collaborators'));
const CollaboratorDetails = lazy(() => import('./Components/Collaborators/Collaborator Details Page/CollaboratorDetails'));
const SuccessPage = lazy(() => import('./Components/CheckOut Page/SuccessPage'));
const Mentions = lazy(() => import('./Components/Mentions Légales/Mentions'));
const CheckoutStatus = lazy(() => import('./Components/CheckOut Page/CheckoutStatus'));
const AdminPanel = lazy(() => import('./Components/Admin Panel/Menu/AdminPanel'));
const AdminLoginPage = lazy(() => import('./Components/Admin Panel/Login Form/LoginPage'));
const TrackOrder = lazy(() => import('./Components/Order Tracking/Track Order/TrackOrder'));
const CategoriesPage = lazy(() => import('./Components/Categories Page/CategoriesPage'));
const SubCategoriesPage = lazy(() => import('./Components/SubCategories Page/SubCategoriesPage'));
const SubSubCategoriesPage = lazy(() => import('./Components/SubSubCategory Page/SubSubCategoriesPage'));
const AffiliateProgramDetails = lazy(() => import('./Components/Account Profile Page/Affiliate Program/AffiliateProgramDetails'));
const Publishers = lazy(() => import('./Components/Collaborators/Publishers Page/Publishers'));
const PublisherDetails = lazy(() => import('./Components/Collaborators/Publisher Details Page/PublisherDetails'));
const BestSellersPage = lazy(() => import('./Components/Best Sellers Page/BestSellersPage'));
const NouveautesPage = lazy(() => import('./Components/Nouveautes Page/NouveautesPage'));
const AddBlog = lazy(() => import('./Components/Blogs Page/Add Blog/AddBlog'));
const BlogDetails = lazy(() => import('./Components/Blogs Page/Blog Details/BlogDetails'));
const BlogPage = lazy(() => import('./Components/Blogs Page/Blogs Page/BlogPage'));
const EditBlog = lazy(() => import('./Components/Blogs Page/Edit Blog/EditBlog'));

const SEO_ROUTE_SECTION_MAP = [
  { test: (pathname) => pathname === '/', sectionId: 27 },
  { test: (pathname) => pathname.startsWith('/products'), sectionId: 30 },
  { test: (pathname) => pathname.startsWith('/bestsellers'), sectionId: 49 },
  { test: (pathname) => pathname.startsWith('/nouveautes'), sectionId: 48 },
  { test: (pathname) => pathname.startsWith('/events') || pathname.startsWith('/eventdetails'), sectionId: 29 },
  { test: (pathname) => pathname.startsWith('/about-us'), sectionId: 28 },
  { test: (pathname) => pathname.startsWith('/contactus'), sectionId: 31 },
  { test: (pathname) => pathname.startsWith('/blogs') || pathname.startsWith('/blogdetails') || pathname.startsWith('/add-blog') || pathname.startsWith('/edit-blog'), sectionId: 8 },
  { test: (pathname) => pathname.startsWith('/collections') || pathname.startsWith('/collection-details'), sectionId: 26 },
  { test: (pathname) => pathname.startsWith('/collaborators') || pathname.startsWith('/collaborator') || pathname.startsWith('/brands'), sectionId: 56 },
  { test: (pathname) => pathname.startsWith('/cart'), sectionId: 11 },
  { test: (pathname) => pathname.startsWith('/checkout') || pathname.startsWith('/order-success'), sectionId: 12 },
  { test: (pathname) => pathname.startsWith('/wishlist'), sectionId: 55 },
  { test: (pathname) => pathname.startsWith('/compare'), sectionId: 14 },
  { test: (pathname) => pathname.startsWith('/account') || pathname.startsWith('/accountprofile') || pathname.startsWith('/affiliate') || pathname.startsWith('/refund_return') || pathname.startsWith('/add_refund_return'), sectionId: 15 },
  { test: (pathname) => pathname.startsWith('/ordertracking') || pathname.startsWith('/followorder') || pathname.startsWith('/review') || pathname.startsWith('/track-order'), sectionId: 16 },
  { test: (pathname) => pathname.startsWith('/legal-information'), sectionId: 57 },
  { test: (pathname) => pathname.startsWith('/login') || pathname.startsWith('/register') || pathname.startsWith('/forget-password') || pathname.startsWith('/reset-password') || pathname.startsWith('/verify-email') || pathname.startsWith('/resend-verify-email'), sectionId: 18 },
];

const CategoryRoute = () => {
  const { subcatname } = useParams();

  return subcatname ? (
    <ScrollToTop><SubCategoriesPage /></ScrollToTop>
  ) : (
    <ScrollToTop><CategoriesPage /></ScrollToTop>
  );
};

// Loading component for better UX
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '50vh',
    fontSize: '16px',
    color: '#671929'
  }}>
    Loading...
  </div>
);

const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID; 
function App() {
  const authctx = useContext(AuthContext)
  const location = useLocation();
  const path = location.pathname;
  const dispatch = useDispatch();
  const [colors, setColors] = useState({
    primaryColor: '#671929',
    secondaryColor: '#E0C389',
    accentColor: '#44202E',
  });
  const [pageMetaData, setPageMetaData] = useState({});

  useEffect(() => {
    const getColors = async () => {
      setColors({
        primaryColor: authctx.companySettings?.primary_color,
        secondaryColor: authctx.companySettings?.secondary_color,
        accentColor: authctx.companySettings?.accent_color,
      });
    };
    getColors();
  }, [authctx.companySettings]);

  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', colors.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', colors.secondaryColor);
    document.documentElement.style.setProperty('--accent-color', colors.accentColor);
  }, [colors]);

  const [isOpen, setIsOpen] = useState(false);
  const [cartisOpen, setcartIsOpen] = useState(false);
  const user = useSelector((state) => state.products.userInfo);
  const adminUser = useSelector((state) => state.products.adminUser);
  const productData = useSelector((state) => state.products.productData);

 const toggle = () => {
   setIsOpen(!isOpen);
 };
 
 const carttoggle = () => {
  if (!cartisOpen) {
    authctx.fetchfavandcartSettings();
  }
  setcartIsOpen(!cartisOpen);
};

useEffect(() => {
  const cs = authctx.companySettings || {};
  document.getElementById('og-facebook').setAttribute('content', cs.facebook || '');
  document.getElementById('og-instagram').setAttribute('content', cs.insta || '');
  document.getElementById('og-twitter').setAttribute('content', cs.twitter || '');
  document.getElementById('og-linkedin').setAttribute('content', cs.linkedin || '');
  document.getElementById('og-youtube').setAttribute('content', cs.youtube || '');
}, [authctx.companySettings]);
const normalizeMetaValue = (value) => (typeof value === 'string' ? value.trim() : '');

const getSectionIdForPath = (pathname) => {
  const matchedRoute = SEO_ROUTE_SECTION_MAP.find((route) => route.test(pathname));
  return matchedRoute?.sectionId ?? null;
};

useEffect(() => {
  if (!path.startsWith('/cp')) {
    localStorage.setItem('route', 'all');
    dispatch(addSelectedCategory(null));
  }
}, [path, dispatch]);

useEffect(() => {
  if (!Array.isArray(productData) || productData.length === 0) return;

  const invalidItems = productData.filter((item) => {
    if (!item || typeof item !== 'object') return true;

    const hasIdentity = Boolean(item.cart_id || item._id || item.id);
    const hasDisplayData = Boolean(item.title || item.name || item.designation || item.image);
    const parsedPrice = Number(item.price_ttc ?? item.price ?? item._prix_public_ttc);
    const hasInvalidPrice = !Number.isFinite(parsedPrice);

    return !hasIdentity || !hasDisplayData || hasInvalidPrice;
  });

  if (invalidItems.length === 0) return;

  invalidItems.forEach((item) => {
    dispatch(deleteItem({
      ...item,
      _id: item?._id ?? item?.id,
    }));
  });
}, [productData, dispatch]);

useEffect(() => {
  const sectionId = getSectionIdForPath(path);

  if (!sectionId) {
    setPageMetaData({});
    return;
  }

  let isMounted = true;

  const fetchMetaData = async () => {
    try {
      const ecomType = process.env.REACT_APP_ECOM_TYPE;
      const response = await axios.get(`${process.env.REACT_APP_TESTING_API}/seo-meta?ecom_type=${ecomType}&section_id=${sectionId}`);
      if (isMounted) {
        setPageMetaData(response?.data?.data?.[0] || {});
      }
    } catch (error) {
      if (isMounted) {
        setPageMetaData({});
      }
    }
  };

  fetchMetaData();

  return () => {
    isMounted = false;
  };
}, [path]);

useEffect(() => {
  function start() {
    gapi.client.init({
      clientId: clientId,
      scope: '',
    });
  }

  gapi.load('client:auth2', start);
}, []);

const metaTitle = normalizeMetaValue(pageMetaData?.meta_title);
const metaDescription = normalizeMetaValue(pageMetaData?.meta_description);
const keyword = normalizeMetaValue(pageMetaData?.keyword);
const secondaryKeyword = normalizeMetaValue(pageMetaData?.secondary_keyword);
const longTrainText = normalizeMetaValue(pageMetaData?.long_train_text);

  // On first load, detect browser language and set selectedLanguage in redux
  useEffect(() => {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      if (saved) return; // already set
      const navLang = navigator.language || navigator.userLanguage || '';
      const lower = navLang.toLowerCase();
      // treat English variants as 'eng', everything else as 'fr'
      const langObj = lower.startsWith('en') ? { Language: 'eng' } : { Language: 'fr' };
      dispatch(changeLanguage(langObj));
      localStorage.setItem('selectedLanguage', langObj.Language);
    } catch (err) {
      // ignore in non-browser environments
    }
  }, [dispatch]);
  return (
    <div className="App">
      <Helmet>
        {metaTitle && <title>{metaTitle}</title>}
        {metaDescription && <meta name="description" content={metaDescription} />}
        {keyword && <meta name="keyword" content={keyword} />}
        {secondaryKeyword && <meta name="secondary_keyword" content={secondaryKeyword} />}
        {longTrainText && <meta name="long_train_text" content={longTrainText} />}
      </Helmet>
      {!window.location.pathname.includes("/admin") && (
      <>
      <Navbar toggle={toggle} carttoggle={carttoggle}/>
       <SideBar isOpen={isOpen} toggle={toggle}  carttoggle={carttoggle}/>
       <CartSidebar isOpen={cartisOpen} toggle={carttoggle}/>
       <CartPopupModal/>
       <AddCartPopup />
      </>
    )}
      
      <div style={{transition: 'opacity 0.3s ease-in-out'}}>
        <Routes>
          <Route path='/' element={<ScrollToTop><HomeOptimized carttoggle={carttoggle}/></ScrollToTop>} />
          <Route path='/cp/:catname/:subcatname/:subsubcatname/:id' element={<ScrollToTop><SubSubCategoriesPage/></ScrollToTop>} />
          <Route path='/cp/:catname/:subcatname/:subsubcatname/:productname/:id' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BookDetailsPage carttoggle={carttoggle}/></Suspense></ScrollToTop>} />
          <Route path='/cp/:catname/:subcatname/:subsubcatname/:productname/:id/:token' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BookDetailsPage carttoggle={carttoggle}/></Suspense></ScrollToTop>} />
          <Route path='/cp/:catname/:subcatname?/:catId' element={<CategoryRoute />} />
          <Route path='/productdetails/:id' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BookDetailsPage carttoggle={carttoggle}/></Suspense></ScrollToTop>} />
          <Route path='/products' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BooksPage carttoggle={carttoggle}/></Suspense></ScrollToTop>} />
          <Route path='/products/subcategory/:id' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BooksPage carttoggle={carttoggle}/></Suspense></ScrollToTop>} />
          <Route path='/cart' element={ <ScrollToTop><Suspense fallback={<PageLoader />}><Cart/></Suspense></ScrollToTop> } />
          <Route path='/checkout' element={<ScrollToTop><Suspense fallback={<PageLoader />}><CheckOut/></Suspense></ScrollToTop>} />
          {/* <Route path='/checkout' element={user ? <ScrollToTop><CheckOut/></ScrollToTop> : <Navigate to="/login" replace />} /> */}
          <Route path='/wishlist' element={<ScrollToTop><Suspense fallback={<PageLoader />}><Favorite carttoggle={carttoggle}/></Suspense></ScrollToTop>} />
          <Route path='/login'  element={<ScrollToTop><Suspense fallback={<PageLoader />}><Login /></Suspense></ScrollToTop>} />
          {authctx?.isCompareEnabled && <Route path='/compare' element={<ScrollToTop><Suspense fallback={<PageLoader />}><Compare /></Suspense></ScrollToTop>} />}
          <Route path='/register'  element={ <ScrollToTop><Suspense fallback={<PageLoader />}><Register /></Suspense></ScrollToTop> } />
          <Route path='/contactus' element={<ScrollToTop><Suspense fallback={<PageLoader />}><ContactUsPage /></Suspense></ScrollToTop>} />
          <Route path='/ordertracking' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><OrderTracking/></Suspense></ScrollToTop> : <Navigate to="/login" replace />}/>
          <Route path='/track-order' element={<ScrollToTop><Suspense fallback={<PageLoader />}><TrackOrder/></Suspense></ScrollToTop>} />
          <Route path='/followorder/:orderId' element={<ScrollToTop><Suspense fallback={<PageLoader />}><FollowOrder/></Suspense></ScrollToTop>} />
          <Route path='/review/:orderId' element={<ScrollToTop><Suspense fallback={<PageLoader />}><Review/></Suspense></ScrollToTop>} />
          <Route path='/reset-password' element={<ScrollToTop><Suspense fallback={<PageLoader />}><ResetPassword/></Suspense></ScrollToTop>} />
          <Route path='/forget-password' element={<ScrollToTop><Suspense fallback={<PageLoader />}><ResetPasswordEmail/></Suspense></ScrollToTop>} />
          <Route path='/verify-email' element={<ScrollToTop><Suspense fallback={<PageLoader />}><VerifyPage/></Suspense></ScrollToTop>} />
          <Route path='/resend-verify-email' element={<ScrollToTop><Suspense fallback={<PageLoader />}><ResendVerifyPage/></Suspense></ScrollToTop>} />
          <Route path='/resetpasswordemail' element={<ScrollToTop><Suspense fallback={<PageLoader />}><ResetPasswordEmail/></Suspense></ScrollToTop>} />
          <Route path="/events" element={<ScrollToTop><Suspense fallback={<PageLoader />}><Event/></Suspense></ScrollToTop>} />
          <Route path="/about-us" element={<ScrollToTop><Suspense fallback={<PageLoader />}><Librairie/></Suspense></ScrollToTop>} />
          <Route path="/eventdetails/:eventId" element={<ScrollToTop><Suspense fallback={<PageLoader />}><EventDetails/></Suspense></ScrollToTop>} />
          <Route path='/account' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><AcountProfile/></Suspense></ScrollToTop> : <Navigate to="/login" replace />} />
          <Route  path='/accountprofile' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><AcountProfile/></Suspense></ScrollToTop> : <Navigate to="/login" replace />} />
          <Route path='/affiliate/:id' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><AffiliateProgramDetails/></Suspense></ScrollToTop> : <Navigate to="/login" replace />} />
          <Route path='/add_refund_return/:order_id' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><AddReturnRefund/></Suspense></ScrollToTop> : <Navigate to="/login" replace />} />
          <Route path='/edit_refund_return' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><AddReturnRefund/></Suspense></ScrollToTop> : <Navigate to="/login" replace />} />
          <Route path='/refund_return' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><ViewRetour/></Suspense></ScrollToTop> : <Navigate to="/login" replace />} />
          <Route path='/refund_return/:returnId' element={user ? <ScrollToTop><Suspense fallback={<PageLoader />}><RetourHistory/></Suspense></ScrollToTop> : <Navigate to="/login" replace />} />
          <Route path='/collections' element={<ScrollToTop><Suspense fallback={<PageLoader />}><CollectionsPage/></Suspense></ScrollToTop>} />
          <Route path='/collection-details/:id' element={<ScrollToTop><Suspense fallback={<PageLoader />}><CollectionDetailsPage/></Suspense></ScrollToTop>} />
          <Route path='/collaborator/:id/details' element={<ScrollToTop><Suspense fallback={<PageLoader />}><CollaboratorDetails/></Suspense></ScrollToTop>} />
          <Route path='/collaborators' element={<ScrollToTop><Suspense fallback={<PageLoader />}><Collaborators/></Suspense></ScrollToTop>} />
          <Route path='/brands' element={<ScrollToTop><Suspense fallback={<PageLoader />}><Publishers/></Suspense></ScrollToTop>} />
          <Route path='/brands/:id/details' element={<ScrollToTop><Suspense fallback={<PageLoader />}><PublisherDetails/></Suspense></ScrollToTop>} />
          <Route path='/bestsellers' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BestSellersPage/></Suspense></ScrollToTop>} />
          <Route path='/nouveautes' element={<ScrollToTop><Suspense fallback={<PageLoader />}><NouveautesPage/></Suspense></ScrollToTop>} />
          <Route path='/order-success' element={<ScrollToTop><Suspense fallback={<PageLoader />}><SuccessPage/></Suspense></ScrollToTop>} />  
          <Route path='/checkout-result' element={<ScrollToTop><Suspense fallback={<PageLoader />}><CheckoutStatus/></Suspense></ScrollToTop>} />        
          <Route path='/collaborators' element={<ScrollToTop><Suspense fallback={<PageLoader />}><Collaborators/></Suspense></ScrollToTop>} />     
          <Route path='/legal-information' element={<ScrollToTop><Suspense fallback={<PageLoader />}><Mentions/></Suspense></ScrollToTop>} />
          <Route path='/edit-blog/:blogId' element={<ScrollToTop><Suspense fallback={<PageLoader />}><EditBlog/></Suspense></ScrollToTop>} />
          <Route path='/add-blog' element={<ScrollToTop><Suspense fallback={<PageLoader />}><AddBlog/></Suspense></ScrollToTop>} />
          <Route path='/blogdetails/:blogId' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BlogDetails/></Suspense></ScrollToTop>} />
          <Route path='/blogs' element={<ScrollToTop><Suspense fallback={<PageLoader />}><BlogPage/></Suspense></ScrollToTop>} />
          <Route path="*" element={<ErrorPage />} />

          <Route path='/admin-panel' element={!adminUser?.email ? <Suspense fallback={<PageLoader />}><AdminLoginPage /></Suspense> : <Suspense fallback={<PageLoader />}><AdminPanel /></Suspense>} />
          <Route path='/admin-panel/:pageId/*' element={!adminUser?.email ? <Suspense fallback={<PageLoader />}><AdminLoginPage /></Suspense> : <Suspense fallback={<PageLoader />}><AdminPanel /></Suspense>} />
        </Routes>
      </div>
      {!window.location.pathname.includes("/admin") && (
      <Footer />
    )}
      <ToastContainer />
    </div>
  );
}

export default App;
